import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import { Icon } from 'js/components';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import useWindowDimensions from 'js/utils/window-dimensions-hook';
import { OrgTreeContext } from './OrgTreeContext';
import { mountOrgTreeDragPreview } from './OrgTreeDragPreview';
import { canStartDragFromOrgTreeRow } from './org-tree-utils';

function TeamNode({ node, pathToNode, depth, onRemove }) {
  const { uniqueContextId } = useContext(OrgTreeContext);
  const dragRef = useRef(null);
  const [showMenu, setShowMenu] = useState(false);
  const windowDims = useWindowDimensions();
  const fromParentId = pathToNode[pathToNode.length - 1]?.id;

  useEffect(() => {
    if (!dragRef.current) return;

    return combine(
      draggable({
        element: dragRef.current,
        canDrag: canStartDragFromOrgTreeRow,
        getInitialData: () => ({
          type: 'team',
          uniqueContextId,
          node,
          fromParentId,
          pathToNode,
        }),
        onGenerateDragPreview: (args) => {
          mountOrgTreeDragPreview(args, {
            kind: 'team',
            name: node.name,
          });
        },
        onDragStart: ({ source }) => {
          source.element.classList.add('org-tree-row--dragging');
        },
        onDrop: ({ source }) => {
          source.element.classList.remove('org-tree-row--dragging');
        },
      })
    );
  }, [uniqueContextId, node, fromParentId, pathToNode]);

  const rowClass = useMemo(
    () => classnames('team-node', 'row', { 'with-menu-open': showMenu }),
    [showMenu]
  );

  return (
    <div className={rowClass} ref={dragRef}>
      <div className="gutters">
        {pathToNode.map((n) => (
          <div
            key={n.depthId}
            className={`gutter ${n.isRoot ? 'org-level' : 'group-level'}`}
          />
        ))}
      </div>
      <div className="icons">
        <Icon className="row-icon team-icon" icon="users" />
      </div>
      <div className="row-content">
        <div className="name">{node.name}</div>
        <div className="actions">
          <Dropdown
            size="sm"
            isOpen={showMenu}
            toggle={() => setShowMenu(!showMenu)}
          >
            <DropdownToggle className="btn btn-link">
              <Icon icon={['far', 'ellipsis-h']} />
            </DropdownToggle>
            <DropdownMenu right={windowDims.width <= 600}>
              <DropdownItem
                data-test="remove-dropdown"
                onClick={() => onRemove(pathToNode, node)}
              >
                <Icon icon="trash-alt" className="mx-1" /> Remove
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>
    </div>
  );
}

export default TeamNode;
